const FLOW_ENTRY_STATE_KEY = "serviceRequestFlowEntry";

export const markDescriptionAsFlowEntry = () => {
  const state = window.history.state as
    | (Record<string, unknown> & { usr?: Record<string, unknown> })
    | null;

  window.history.replaceState(
    {
      ...state,
      usr: { ...state?.usr, [FLOW_ENTRY_STATE_KEY]: true },
    },
    "",
    window.location.href
  );
};

/**
 * Whether this document was loaded by a back or forward gesture rather than
 * navigated to (RT-4321).
 *
 * The description screen is the flow's entry, so it is reachable both ways, and
 * it has to tell them apart: arriving fresh with a draft already on the device
 * means offering to resume or discard it, while *stepping back* onto it means
 * the visitor is still filling that very draft in and must not be asked.
 *
 * Every back press inside the widget carries `from_step=1` instead. This
 * covers the one it cannot: a back that crosses into the document the flow was
 * entered from restores that URL exactly as it was first loaded — no query
 * param of ours on it, and none of our code ran to put one there.
 *
 * `back_forward` is the Navigation Timing type for exactly that, page-cache
 * restores included. Unsupported or unavailable (an empty entry list, jsdom)
 * reads as a normal navigation, which is the safe answer: the visitor gets
 * asked rather than silently resuming.
 */
export const isBackForwardNavigation = (): boolean => {
  const state = window.history.state as
    | { usr?: Record<string, unknown> }
    | null;

  if (state?.usr?.[FLOW_ENTRY_STATE_KEY] === true) return true;

  try {
    const [entry] = window.performance.getEntriesByType("navigation");

    return (entry as PerformanceNavigationTiming | undefined)?.type ===
      "back_forward";
  } catch {
    return false;
  }
};
